import { InvoiceStatus } from "../../Orders/enums";
import { Shipment } from "../shipment.interface";
import type { ShipmentDetails } from "./types";

const JOURNEY_STEPS: Array<{
  status: string;
  timelineKey: string | null;
  remarks: string;
}> = [
  {
    status: "Shipment Created",
    timelineKey: null,
    remarks: "Auto parts shipment created and orders consolidated",
  },
  {
    status: "Booked At",
    timelineKey: "bookedAt",
    remarks: "Delivery booking confirmed with courier partner",
  },
  {
    status: "Assigned to Agent",
    timelineKey: "assignedToAgentAt",
    remarks: "Shipment assigned to delivery agent",
  },
  {
    status: "Package Picked Up",
    timelineKey: "pickedUpAt",
    remarks: "Package collected by delivery partner",
  },
  {
    status: "Package Delivered",
    timelineKey: "deliveredAt",
    remarks: "Successfully delivered to customer",
  },
  {
    status: "Package Cancelled",
    timelineKey: "cancelledAt",
    remarks: "Shipment was cancelled",
  },
];

function buildStatusHistory(
  shipment: Shipment
): ShipmentDetails["statusHistory"] {
  const createdAtDate = shipment.createdAt
    ? new Date(shipment.createdAt)
    : new Date();

  const timeline: Record<string, unknown> = {
    ...(shipment.deliveryTracking?.timeline as
      | Record<string, unknown>
      | undefined),
    bookedAt: shipment.deliveryBooking?.bookedAt,
  };

  return JOURNEY_STEPS.map((step) => {
    let timestamp = "-";
    let hasData = false;

    if (step.timelineKey === null) {
      timestamp = createdAtDate.toLocaleString();
      hasData = true;
    } else if (timeline && timeline[step.timelineKey]) {
      try {
        const eventDate = new Date(
          timeline[step.timelineKey] as string | number | Date
        );
        timestamp = eventDate.toLocaleString();
        hasData = true;
      } catch {
        // keep defaults
      }
    }

    return {
      status: step.status,
      timestamp,
      remarks: step.remarks,
      hasData,
    };
  });
}

function formatOptionalDate(
  value: string | number | Date | undefined | null,
  fallback: string
): string {
  if (!value) return fallback;
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return "Invalid date";
  }
}

export function mapApiShipmentToShipmentDetails(
  latestShipment: Shipment
): ShipmentDetails {
  const createdAtDate = latestShipment.createdAt
    ? new Date(latestShipment.createdAt)
    : new Date();

  return {
    id: latestShipment.shipmentId,
    shipmentNo: latestShipment.shipmentId,
    orderNo: latestShipment.orderNumbers?.join(", "),
    date: createdAtDate.toLocaleDateString() || "",
    customerName: latestShipment.dropDetails?.address?.contactDetails?.name,
    customerType: "Auto Parts Dealer",
    destinationAddress: `${latestShipment.dropDetails?.address?.streetAddress1}, ${latestShipment.dropDetails?.address?.city}, ${latestShipment.dropDetails?.address?.state}`,
    pickupAddress: `${latestShipment.pickupDetails?.address?.streetAddress1}, ${latestShipment.pickupDetails?.address?.city}, ${latestShipment.pickupDetails?.address?.state}`,
    courier: "Porter",
    trackingNo: latestShipment.deliveryBooking?.providerOrderId,
    trackingUrl: latestShipment.deliveryBooking?.trackingUrl,
    status: latestShipment.status,
    statusHistory: buildStatusHistory(latestShipment),
    lastUpdatedAt: latestShipment.deliveryTracking?.lastUpdatedAt
      ? new Date(
          latestShipment.deliveryTracking.lastUpdatedAt as unknown as string
        ).toLocaleString()
      : "-",
    estimatedDelivery: formatOptionalDate(
      latestShipment.deliveryBooking?.estimatedDeliveryTime,
      "Not available"
    ),
    actualDelivery: formatOptionalDate(
      latestShipment.deliveryTracking?.timeline?.deliveredAt,
      "-"
    ),
    deliveryInstructions:
      latestShipment.deliveryBooking?.deliveryInstructions || "-",
    packageDetails: {
      orderCount: latestShipment.orderNumbers?.length || 0,
      totalItemsQuantity: latestShipment.totalQuantity || 0,
    },
    billingDetails: {
      shippingCharges:
        latestShipment.deliveryBooking?.estimatedFare?.minorAmount || 0,
    },
    contactDetails: {
      senderName: latestShipment.pickupDetails?.address?.contactDetails?.name,
      senderPhone:
        latestShipment.pickupDetails?.address?.contactDetails?.phoneNumber,
      receiverName: latestShipment.dropDetails?.address?.contactDetails?.name,
      receiverPhone:
        latestShipment.dropDetails?.address?.contactDetails?.phoneNumber,
    },
    orders:
      (latestShipment as any).orders?.length > 0
        ? (latestShipment as any).orders.map((ord: any, index: number) => {
            const inv = ord?.invoiceInfo;
            const activeSupplierAllocation = ord?.allocatedTo?.find(
              (allocation: any) => allocation.isActive
            );

            const supplierInv = activeSupplierAllocation?.invoiceInfo;
            const garaazInvoiceMediaId =
              inv?.invoiceMediaId?.$oid || inv?.invoiceMediaId;
            const supplierInvoiceMediaId =
              supplierInv?.invoiceMediaId?.$oid ||
              supplierInv?.invoiceMediaId;

            return {
              id: `${latestShipment.shipmentId}-order-${index}`,
              orderNo: ord.orderNo,
              date: ord.orderDate
                ? new Date(ord.orderDate).toLocaleDateString()
                : "-",
              customerName:
                latestShipment.dropDetails.address.contactDetails.name || "-",
              branchName: "-",
              deliveryMode: ord.deliveryMode || "-",
              invoiceNo: inv?.invoiceNo || "-",
              invoiceMediaId: garaazInvoiceMediaId || "-",
              invoiceAmount: Number(inv?.invoiceAmount) || 0,
              invoiceStatus: inv?.invoiceStatus || InvoiceStatus.UNPAID,
              paymentMode: inv?.payment?.mode || "-",
              paymentCollectedBy: inv?.payment?.collectedBy || "-",
              paymentCollectedAt: inv?.payment?.collectedAt || "-",
              supplierInvoiceNo: supplierInv?.invoiceNo || "-",
              supplierInvoiceMediaId: supplierInvoiceMediaId || "-",
              supplierInvoiceAmount: Number(supplierInv?.invoiceAmount) || 0,
              items: Array.isArray(ord?.orderItems)
                ? ord.orderItems.map((item: any) => ({
                    partId: item?.partId || "-",
                    rootPartNumber: item?.rootPartNumber || "-",
                    givenQuantity: item?.givenQuantity || 0,
                    orderedQuantity: item?.orderedQuantity || 0,
                  }))
                : [],
              status: ord.orderStatus,
            } as ShipmentDetails["orders"][number];
          })
        : [],
  };
}
